const CONTACT_EMAIL = "info@cobar.app";

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t pt-6">
      <h2 className="text-sm font-medium uppercase tracking-wider mb-4">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Step({
  number,
  children,
}: {
  number: number;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4 text-sm text-muted-foreground">
      <span className="font-mono text-xs pt-0.5 text-muted-foreground/50 shrink-0">
        {number}.
      </span>
      <span>{children}</span>
    </div>
  );
}

export default function Beta() {
  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-xl font-medium uppercase tracking-wider">Beta</h1>

      <p className="text-sm text-muted-foreground">
        Thanks for being part of the Cobar beta. Your feedback helps us improve.
        For anything at all —{" "}
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="text-foreground underline underline-offset-4 hover:text-primary"
        >
          {CONTACT_EMAIL}
        </a>
        .
      </p>

      <Section title="Reporting Bugs">
        <p className="text-sm text-muted-foreground mb-4">
          When something doesn&apos;t work as expected, the most useful bug
          report includes:
        </p>
        <div className="space-y-3">
          <Step number={1}>
            <span>
              <strong className="text-foreground font-medium">
                What you were doing
              </strong>{" "}
              — the sequence of actions that led to the problem. The more
              specific, the better.
            </span>
          </Step>
          <Step number={2}>
            <span>
              <strong className="text-foreground font-medium">
                What you expected to happen
              </strong>{" "}
              and what actually happened instead.
            </span>
          </Step>
          <Step number={3}>
            <span>
              <strong className="text-foreground font-medium">
                Whether it&apos;s reproducible
              </strong>{" "}
              — does it happen every time, or only occasionally? If it&apos;s
              intermittent, any pattern you&apos;ve noticed helps.
            </span>
          </Step>
          <Step number={4}>
            <span>
              <strong className="text-foreground font-medium">
                Your setup
              </strong>{" "}
              — macOS version, whether you&apos;re using the standalone app or
              the plugin (AU/VST3), and which DAW if applicable.
            </span>
          </Step>
        </div>
        <p className="text-sm text-muted-foreground mt-4">
          Email your report to{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-foreground underline underline-offset-4 hover:text-primary"
          >
            {CONTACT_EMAIL}
          </a>
          . Screenshots or screen recordings are always welcome.
        </p>
      </Section>

      <Section title="Crash Reports">
        <p className="text-sm text-muted-foreground mb-4">
          When Cobar crashes, macOS automatically generates a crash report
          containing a detailed stack trace. That trace is the most valuable
          thing you can send us — it tells us exactly where in the code
          something went wrong.
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          When the crash dialog appears:
        </p>
        <div className="space-y-3">
          <Step number={1}>
            Click{" "}
            <strong className="text-foreground font-medium">Report…</strong> (or{" "}
            <strong className="text-foreground font-medium">
              Report to Apple
            </strong>{" "}
            — the label varies by macOS version). This reveals the full crash
            report in a scrollable text area.
          </Step>
          <Step number={2}>
            Select all of the text in that panel and copy it.
          </Step>
          <Step number={3}>
            Email it to{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-foreground underline underline-offset-4 hover:text-primary"
            >
              {CONTACT_EMAIL}
            </a>{" "}
            along with a brief description of what you were doing when it
            crashed.
          </Step>
          <Step number={4}>
            Click{" "}
            <strong className="text-foreground font-medium">
              Don&apos;t Send
            </strong>{" "}
            to close the dialog without submitting to Apple. Reporting to Apple
            doesn&apos;t reach us — they don&apos;t share crash reports for apps
            outside the App Store.
          </Step>
        </div>
        <p className="text-sm text-muted-foreground mt-4">Thank you!</p>
      </Section>
    </div>
  );
}
